import { useEffect, useRef, useState } from 'react'
import { TUTORIAL_STEPS, TUTORIAL_STEP_ORDER } from '../game/tutorial'
import type { TutorialStepId } from '../game/types'
import { useI18n } from '../i18n/useI18n'
import { useGameStore } from '../store/useGameStore'
import type { TabId } from './BottomNav'
import { CoachMark } from './CoachMark'

const SPOTLIGHT_PADDING = 6
const TOOLTIP_WIDTH = 320
const TOOLTIP_MARGIN = 12

type Rect = { top: number; left: number; width: number; height: number }

type Props = {
  setTab: (tab: TabId) => void
}

export function TutorialOverlay({ setTab }: Props) {
  const { t } = useI18n()
  const tutorialStep = useGameStore((s) => s.tutorialStep)
  const advanceTutorial = useGameStore((s) => s.advanceTutorial)
  const [spotlightRect, setSpotlightRect] = useState<Rect | null>(null)
  const rafRef = useRef<number | null>(null)

  const stepId = tutorialStep && tutorialStep !== 'done' ? (tutorialStep as TutorialStepId) : null
  const stepDef = stepId ? TUTORIAL_STEPS[stepId] : null
  const stepIndex = stepId ? TUTORIAL_STEP_ORDER.indexOf(stepId) : -1
  const totalSteps = TUTORIAL_STEP_ORDER.length

  useEffect(() => {
    if (stepDef) setTab(stepDef.tab)
  }, [stepId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!stepDef) {
      setSpotlightRect(null)
      return
    }

    const measure = () => {
      const el = document.querySelector(`[data-tutorial-id="${stepDef.targetId}"]`)
      if (!el) return
      const r = el.getBoundingClientRect()
      setSpotlightRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }

    // Double rAF: first frame lets React commit the tab switch, second ensures layout
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(measure)
    })

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [stepId, stepDef])

  useEffect(() => {
    if (!stepDef) return
    const onResize = () => {
      const el = document.querySelector(`[data-tutorial-id="${stepDef.targetId}"]`)
      if (!el) return
      const r = el.getBoundingClientRect()
      setSpotlightRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [stepDef])

  if (!stepDef || !spotlightRect) return null

  const sp = {
    top: spotlightRect.top - SPOTLIGHT_PADDING,
    left: spotlightRect.left - SPOTLIGHT_PADDING,
    width: spotlightRect.width + SPOTLIGHT_PADDING * 2,
    height: spotlightRect.height + SPOTLIGHT_PADDING * 2,
  }

  const viewportHeight = window.innerHeight
  const viewportWidth = window.innerWidth
  const tooltipLeft = Math.max(8, Math.min(viewportWidth - TOOLTIP_WIDTH - 8, sp.left + sp.width / 2 - TOOLTIP_WIDTH / 2))

  let tooltipTop: number
  const spaceBelow = viewportHeight - sp.top - sp.height
  const spaceAbove = sp.top
  const position = stepDef.tooltipPosition

  if (position === 'above' || (position === 'auto' && spaceAbove > spaceBelow)) {
    tooltipTop = sp.top - TOOLTIP_MARGIN - 200 // will be clipped upward naturally
  } else {
    tooltipTop = sp.top + sp.height + TOOLTIP_MARGIN
  }
  tooltipTop = Math.max(8, Math.min(viewportHeight - 8, tooltipTop))

  return (
    <div
      className="fixed inset-0 z-[60]"
      style={{ pointerEvents: 'all' }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Spotlight: transparent hole with shadow creating the dim surround */}
      <div
        style={{
          position: 'fixed',
          top: sp.top,
          left: sp.left,
          width: sp.width,
          height: sp.height,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
          borderRadius: 10,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Tooltip */}
      <div
        style={{
          position: 'fixed',
          top: tooltipTop,
          left: tooltipLeft,
          width: TOOLTIP_WIDTH,
          zIndex: 2,
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <CoachMark
          label={`${t('tutorial_label')} ${stepIndex + 1}/${totalSteps}`}
          title={t(stepDef.titleKey)}
          body={t(stepDef.bodyKey)}
          cta={stepIndex < totalSteps - 1 ? t('tutorial_next') : t('tutorial_finish')}
          onDismiss={advanceTutorial}
        />
      </div>
    </div>
  )
}
